import { Badge, Spinner, Text, mergeClasses } from "@fluentui/react-components";
import type { MeetingReport } from "../services/apiService";
import {
  useUiStyles,
  SectionTitle,
  IconTile,
  MetaRow,
  DocumentIcon,
  ClockIcon,
  ArrowRightIcon,
} from "./ui";
import { useLanguage } from "../i18n/LanguageContext";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function statusBadge(report: MeetingReport, t: (path: string) => string) {
  if (report.processStatus === "Completed" && report.isTranscript) {
    return (
      <Badge appearance="tint" color="success" size="small">
        {t("reports.summaryReady")}
      </Badge>
    );
  }
  if (report.processStatus === "Failed" || report.processStatus === "FailedRetryable") {
    return (
      <Badge appearance="tint" color="danger" size="small">
        {t("reports.failed")}
      </Badge>
    );
  }
  if (report.isTranscript) {
    return (
      <Badge appearance="tint" color="brand" size="small">
        {t("reports.processing")}
      </Badge>
    );
  }
  return (
    <Badge appearance="tint" color="informative" size="small">
      {t("reports.noTranscript")}
    </Badge>
  );
}

interface ReportsListProps {
  reports: MeetingReport[];
  loading: boolean;
  onOpen: (report: MeetingReport) => void;
}

/**
 * Read-AI-style list of processed meetings coming from the Meeting Angelo
 * database. Rows with a ready summary open the full report view.
 */
export function ReportsList({ reports, loading, onOpen }: ReportsListProps) {
  const s = useUiStyles();
  const { t } = useLanguage();

  return (
    <section className={s.section}>
      <SectionTitle count={reports.length}>{t("reports.title")}</SectionTitle>

      {loading && (
        <div className={s.card}>
          <Spinner size="tiny" label={t("reports.loading")} />
        </div>
      )}

      {!loading && reports.length === 0 && (
        <div className={mergeClasses(s.card, s.emptyCard)}>
          <IconTile variant="neutral">
            <DocumentIcon />
          </IconTile>
          <Text size={200}>
            {t("reports.empty")}
          </Text>
        </div>
      )}

      {!loading && reports.length > 0 && (
        <div className={s.stack}>
          {reports.map((report) => {
            const clickable = report.isTranscript;
            const ready = report.processStatus === "Completed" && report.isTranscript;
            return (
              <div
                key={report.eventId}
                className={mergeClasses(
                  s.card,
                  s.accentLeft,
                  clickable && s.cardInteractive,
                )}
                onClick={clickable ? () => onOpen(report) : undefined}
                role={clickable ? "button" : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={
                  clickable
                    ? (e) => (e.key === "Enter" || e.key === " ") && onOpen(report)
                    : undefined
                }
              >
                <div className={s.row}>
                  <IconTile variant={ready ? "success" : "brand"}>
                    <DocumentIcon />
                  </IconTile>
                  <div className={s.grow}>
                    <div className={s.rowBetween}>
                      <Text weight="semibold" block truncate wrap={false} className={s.grow}>
                        {report.name}
                      </Text>
                      {statusBadge(report, t)}
                    </div>
                    <MetaRow>
                      <ClockIcon />
                      <span>
                        {formatDate(report.startDateTime)} · {formatTime(report.startDateTime)}–
                        {formatTime(report.endDateTime)}
                        {report.participantCount > 0
                          ? ` · ${report.participantCount} ${t("reports.participants")}`
                          : ""}
                      </span>
                    </MetaRow>
                  </div>
                  {clickable && (
                    <span style={{ alignSelf: "center", color: "var(--colorNeutralForeground3)" }}>
                      <ArrowRightIcon />
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
